// =============================================================================
// VandieZ shipper-web -- Address book (/addresses)
// =============================================================================
// CU-18. Every load post used to re-enter pickup and drop from scratch; the
// only address we stored was the single KYC one.
//
// "Default" means exactly one, enforced by a partial unique index on the
// server. Promoting an address clears the previous default in the same
// transaction -- which is why this page never tries to un-set a default
// directly, only to promote a different one. The server refuses the former
// with DEFAULT_MUST_EXIST, and it is right to: a book that pre-fills nothing
// is the state CU-18 exists to remove.
// =============================================================================

import { Loader2, MapPin, Plus, Star, Trash2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { addressesApi } from "@/api/client";
import type { SavedAddress, SavedAddressPayload } from "@/api/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";

const EMPTY: SavedAddressPayload = {
  label: "",
  address: "",
  district: "",
  lat: "",
  lng: "",
};

export function AddressesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<SavedAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SavedAddressPayload>(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) return;
      setLoading(true);
      try {
        const res = await addressesApi.list(accessToken, signal);
        setItems(res.items);
        setError(null);
      } catch (err) {
        if (signal?.aborted === true) return;
        setError(
          err instanceof ApiError ? err.message : "Could not load addresses.",
        );
      } finally {
        if (signal?.aborted !== true) setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); };
  }, [load]);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (accessToken === null) return;
    setBusy(true);
    setError(null);
    try {
      await addressesApi.create(accessToken, form);
      setForm(EMPTY);
      setShowForm(false);
      await load();
    } catch (err) {
      // ADDRESS_LABEL_TAKEN is the common one and its message names the
      // label, so surface it verbatim rather than a generic failure.
      setError(
        err instanceof ApiError ? err.message : "Could not save the address.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function act(fn: () => Promise<unknown>): Promise<void> {
    if (accessToken === null) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Saved addresses</h1>
          <p className="text-sm text-muted-foreground">
            Your default address pre-fills the pickup on a new load.
          </p>
        </div>
        <Button size="sm" onClick={() => { setShowForm((v) => !v); }}>
          <Plus className="mr-1 h-4 w-4" /> Add address
        </Button>
      </div>

      {error !== null ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {showForm ? (
        <form
          onSubmit={(e) => void submit(e)}
          className="space-y-4 rounded-lg border bg-background p-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Label"
              value={form.label}
              onChange={(v) => { setForm({ ...form, label: v }); }}
              placeholder="Home, Warehouse, Mill…"
              required
            />
            <Field
              label="District"
              value={form.district}
              onChange={(v) => { setForm({ ...form, district: v }); }}
              placeholder="Chennai"
              required
            />
            <div className="sm:col-span-2">
              <Field
                label="Address"
                value={form.address}
                onChange={(v) => { setForm({ ...form, address: v }); }}
                placeholder="Street, area, landmark"
                required
              />
            </div>
            <Field
              label="Latitude"
              value={form.lat}
              onChange={(v) => { setForm({ ...form, lat: v }); }}
              placeholder="13.0827000"
              required
            />
            <Field
              label="Longitude"
              value={form.lng}
              onChange={(v) => { setForm({ ...form, lng: v }); }}
              placeholder="80.2707000"
              required
            />
            <Field
              label="Contact name (optional)"
              value={form.contact_name ?? ""}
              onChange={(v) => { setForm({ ...form, contact_name: v || null }); }}
            />
            <Field
              label="Contact phone (optional)"
              value={form.contact_phone ?? ""}
              onChange={(v) => { setForm({ ...form, contact_phone: v || null }); }}
              placeholder="+919900000000"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default ?? false}
              onChange={(e) => { setForm({ ...form, is_default: e.target.checked }); }}
            />
            Make this my default
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setShowForm(false); }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
          No saved addresses yet. The first one you add becomes your default
          automatically.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div
              key={a.id}
              className="flex items-start justify-between gap-4 rounded-lg border bg-background p-4"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-medium">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {a.label}
                  {a.is_default ? (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      Default
                    </span>
                  ) : null}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {a.address}, {a.district}
                </p>
                {a.contact_name !== null ? (
                  <p className="text-sm text-muted-foreground">
                    {a.contact_name}
                    {a.contact_phone !== null ? ` · ${a.contact_phone}` : ""}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-2">
                {/*
                  Only PROMOTION is offered, never demotion. The server
                  refuses un-setting a default outright, because a populated
                  book with no default is the pre-CU-18 experience returning
                  by the back door.
                */}
                {!a.is_default ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      void act(() =>
                        addressesApi.makeDefault(accessToken ?? "", a.id),
                      )
                    }
                  >
                    <Star className="mr-1 h-3.5 w-3.5" /> Make default
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void act(() => addressesApi.remove(accessToken ?? "", a.id))
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}): React.JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input
        className="w-full rounded-md border bg-background px-3 py-2"
        value={value}
        placeholder={placeholder ?? ""}
        required={required ?? false}
        onChange={(e) => { onChange(e.target.value); }}
      />
    </label>
  );
}

export default AddressesPage;
